import React, { useState } from "react";
import {
  UnfoldLessOutlined,
  UnfoldMoreOutlined,
  ViewColumnOutlined,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsMenu as ShowHideColumnsMenu,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";

const PRIMARY_COLOR = "#0b5cab";

const normalizeSearchText = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleDateString("vi-VN");

  if (Array.isArray(value)) {
    return value.map(normalizeSearchText).filter(Boolean).join(" ");
  }

  if (typeof value === "object") {
    return Object.values(value).map(normalizeSearchText).filter(Boolean).join(" ");
  }

  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .trim();
};

const dataTableGlobalFilterFn = (row, _columnId, filterValue) => {
  const query = normalizeSearchText(filterValue);
  if (!query) return true;

  return normalizeSearchText(row.original).includes(query);
};

// const getExportValue = (value) => {
//   if (value === null || value === undefined) return "";
//   if (value instanceof Date) return value;
//   if (["string", "number", "boolean"].includes(typeof value)) return value;
//   return JSON.stringify(value);
// };

export default function DataTable({
  columns,
  data,
  enableExpanding = false,
  getSubRows,
  onExpandAll,
  onCollapseAll,
  enableEditing = false,
  editDisplayMode = "modal",
  renderDetailPanel,
  enableRowActions = false,
  enableRowSelection = false,
  renderRowActions,
  initialState = {},
  state,
  onRowSelectionChange,
  onPaginationChange,
  onGlobalFilterChange,
  manualPagination = false,
  manualFiltering = false,
  rowCount,
  // enableExport = false,
  // exportFileName = "du-lieu",
  enableSorting = false,
  enableColumnActions = false,
  enableGlobalFilter = true,
  enablePagination = true,
  enableBottomToolbar = true,
  rowsPerPageOptions = [5, 10, 20, 50],
  muiTablePaperProps,
  muiTableContainerProps,
  muiTableBodyCellProps,
  muiTableBodyRowProps,
  onRowClick,
  maxTableHeight = "min(520px, calc(100vh - 280px))",
  renderTopToolbarExtraActions,
  getRowId,
  ...tableOptions
}) {
  const {
    filterFns: customFilterFns,
    globalFilterFn: customGlobalFilterFn,
    ...restTableOptions
  } = tableOptions;
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState(
    initialState?.globalFilter
  );
  const [internalPagination, setInternalPagination] = useState({
    pageIndex: initialState?.pagination?.pageIndex ?? 0,
    pageSize: initialState?.pagination?.pageSize ?? 10,
  });
  const pagination = state?.pagination ?? internalPagination;
  const globalFilter = state?.globalFilter ?? internalGlobalFilter;
  const handlePaginationChange = (updater) => {
    const nextPagination =
      typeof updater === "function" ? updater(pagination) : updater;

    setInternalPagination(nextPagination);
    onPaginationChange?.(updater);
  };
  const handleGlobalFilterChange = (updater) => {
    const nextGlobalFilter =
      typeof updater === "function" ? updater(globalFilter) : updater;

    if (state?.globalFilter === undefined) {
      setInternalGlobalFilter(nextGlobalFilter);
    }

    if (!manualPagination) {
      handlePaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    }

    onGlobalFilterChange?.(updater);
  };

  const getTableBodyRowProps = (args) => {
    const customProps = typeof muiTableBodyRowProps === "function"
      ? muiTableBodyRowProps(args) || {}
      : muiTableBodyRowProps || {};
    const isClickable = typeof onRowClick === "function";
    const isInteractiveTarget = (event) =>
      event.target !== event.currentTarget &&
      event.target.closest?.(
        "a, button, input, select, textarea, [role='button'], [role='link'], [contenteditable='true']",
      );

    return {
      ...customProps,
      ...(isClickable ? { tabIndex: customProps.tabIndex ?? 0 } : {}),
      onClick: (event) => {
        customProps.onClick?.(event);
        if (!event.defaultPrevented && isClickable && !isInteractiveTarget(event)) {
          onRowClick(args.row.original, args.row, event);
        }
      },
      onKeyDown: (event) => {
        customProps.onKeyDown?.(event);
        if (
          !event.defaultPrevented &&
          isClickable &&
          !isInteractiveTarget(event) &&
          (event.key === "Enter" || event.key === " ")
        ) {
          event.preventDefault();
          onRowClick(args.row.original, args.row, event);
        }
      },
      sx: {
        "&:hover td": { backgroundColor: "#f6f9fc" },
        ...(isClickable && {
          cursor: "pointer",
          "&:focus-visible": {
            outline: `2px solid ${PRIMARY_COLOR}`,
            outlineOffset: -2,
          },
        }),
        ...(customProps.sx || {}),
      },
    };
  };

  const table = useMaterialReactTable({
    columns,
    data: data || [],
    localization: MRT_Localization_VI,
    layoutMode: "semantic",
    defaultColumn: {
      minSize: 20,
      size: 150,
      maxSize: 420,
    },
    getRowId,
    displayColumnDefOptions: {
      "mrt-row-expand": {
        size: 36,
        minSize: 36,
        maxSize: 36,
      },
      "mrt-row-actions": {
        size: 80,
        minSize: 50,
        maxSize: 100,
      },
    },
    enableExpanding,
    enableExpandAll: false,
    getSubRows,
    enableEditing,
    editDisplayMode,
    renderDetailPanel,
    enableRowActions,
    enableRowSelection,
    positionActionsColumn: "last",
    renderRowActions,
    onRowSelectionChange,
    onPaginationChange: handlePaginationChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    manualPagination,
    manualFiltering,
    rowCount,
    filterFns: {
      dataTableGlobal: dataTableGlobalFilterFn,
      ...(customFilterFns || {}),
    },
    globalFilterFn: customGlobalFilterFn ?? "dataTableGlobal",

    enableSorting,
    enableColumnActions,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: true,
    enableStickyHeader: true,
    enableHiding: true,
    enableGlobalFilter,
    positionGlobalFilter: "right",
    enablePagination,
    enableBottomToolbar,
    paginationDisplayMode: "pages",

    renderTopToolbarCustomActions: ({ table: currentTable }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Tooltip title="Thu gọn tất cả">
          <span>
            <IconButton
              aria-label="Thu gọn tất cả"
              disabled={!enableExpanding && !onCollapseAll}
              onClick={() => onCollapseAll ? onCollapseAll() : currentTable.setExpanded(false)}
              size="small"
            >
              <UnfoldLessOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Mở rộng tất cả">
          <span>
            <IconButton
              aria-label="Mở rộng tất cả"
              disabled={!enableExpanding && !onExpandAll}
              onClick={() => onExpandAll ? onExpandAll() : currentTable.setExpanded(true)}
              size="small"
            >
              <UnfoldMoreOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {renderTopToolbarExtraActions?.({ table: currentTable })}
      </Box>
    ),
    renderToolbarInternalActions: ({ table: currentTable }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <ToggleGlobalFilterButton table={currentTable} />
        <Tooltip title="Ẩn/hiện cột">
          <IconButton
            aria-label="Ẩn/hiện cột"
            onClick={(event) => setColumnMenuAnchor(event.currentTarget)}
            size="small"
          >
            <ViewColumnOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <ShowHideColumnsMenu
          anchorEl={columnMenuAnchor}
          setAnchorEl={setColumnMenuAnchor}
          table={currentTable}
          PaperProps={{
            elevation: 4,
            sx: {
              width: 280,
              maxHeight: 340,
              mt: 0.75,
              border: "1px solid #dfe6ed",
              borderRadius: 1.5,
              overflowY: "auto",
              boxShadow: "0 8px 24px rgba(15, 23, 42, .14)",
              "& .MuiMenuItem-root": {
                minHeight: 36,
                px: 1.5,
                py: 0.5,
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                color: "#263442",
              },
              "& .MuiFormControlLabel-label": {
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
              },
              "& .MuiButton-root": {
                fontFamily: "Inter, sans-serif",
                fontSize: 11,
                textTransform: "none",
              },
            },
          }}
        />
        <ToggleFullScreenButton table={currentTable} />
      </Box>
    ),
    renderBottomToolbarCustomActions: ({ table: currentTable }) => {
      const { pageIndex, pageSize } = currentTable.getState().pagination;
      const total = manualPagination
        ? rowCount ?? currentTable.getPrePaginationRowModel().rows.length
        : currentTable.getFilteredRowModel().rows.length;
      const from = total === 0 ? 0 : pageIndex * pageSize + 1;
      const to = Math.min((pageIndex + 1) * pageSize, total);

      return (
        <Typography sx={{ color: "#5f6b78", fontSize: 12 }}>
          Hiển thị {from.toLocaleString("vi-VN")}–{to.toLocaleString("vi-VN")} trên{" "}
          {total.toLocaleString("vi-VN")} bản ghi
        </Typography>
      );
    },

    muiTablePaperProps: {
      elevation: 0,
      ...muiTablePaperProps,
      sx: {
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        border: "1px solid #dfe6ed",
        borderRadius: 1,
        overflow: "hidden",
        boxShadow: "none",
        ...(muiTablePaperProps?.sx || {}),
      },
    },
    muiTableContainerProps: {
      ...muiTableContainerProps,
      sx: {
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        maxHeight: maxTableHeight,
        overflowX: "auto",
        overflowY: "auto",
        ...(muiTableContainerProps?.sx || {}),
      },
    },
    muiTableProps: {
      stickyHeader: true,
      sx: {
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
        tableLayout: "fixed",
      },
    },
    muiTableBodyProps: {
      sx: {
        width: "100%",
      },
    },
    muiTopToolbarProps: {
      sx: {
        minHeight: 40,
        px: 1,
        py: 0.5,
        borderBottom: "1px solid #dfe6ed",
        backgroundColor: "#fff",
        "& .MuiIconButton-root": { color: "#252b32" },
        "& .MuiIconButton-root.Mui-disabled": { color: "#aab2bb" },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Tìm kiếm...",
      size: "small",
      sx: {
        minWidth: { xs: 180, sm: 280 },
        "& .MuiInputBase-root": {
          height: 34,
          borderRadius: 1.25,
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: PRIMARY_COLOR,
        color: "#fff",
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.3,
        verticalAlign: "center",
        py: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        wordBreak: "break-word",
        borderBottom: 0,
        borderRight: "1px solid rgba(255,255,255,.25)",
        position: "sticky",
        top: 0,
        zIndex: 2,
        "&:last-of-type": { borderRight: 0 },
        "& .MuiIconButton-root": { color: "#fff" },
        textAlign: "center",
        "& .Mui-TableHeadCell-Content": {
          justifyContent: "center",
        },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "normal",
          wordBreak: "break-word",
          textOverflow: "clip",
        },
      },
    },
    muiTableBodyRowProps: getTableBodyRowProps,
    muiTableBodyCellProps:
      typeof muiTableBodyCellProps === "function"
        ? (args) => {
          const customProps = muiTableBodyCellProps(args) || {};
          return {
            ...customProps,
            align: 'center',
            sx: {
              py: 0.9,
              color: "#1f2937",
              fontSize: 12,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              wordBreak: "break-word",
              border: 0,
              borderBottom: "1px solid #edf0f3",
              ...(customProps.sx || {}),
            },
          };
        }
        : {
          ...muiTableBodyCellProps,
          align: "center",
          sx: {
            py: 0.9,
            color: "#1f2937",
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            wordBreak: "break-word",
            border: 0,
            borderBottom: "1px solid #edf0f3",
            ...(muiTableBodyCellProps?.sx || {}),
          },
        },
    muiBottomToolbarProps: {
      sx: {
        minHeight: 52,
        px: 1,
        borderTop: "1px solid #edf0f3",
        backgroundColor: "#fff",
        "& .MuiTablePagination-root": {
          width: "auto",
          py: 0.5,
        },
        "& .MuiPaginationItem-root": {
          minWidth: 28,
          height: 28,
          borderRadius: 1,
          fontSize: 11,
        },
        "& .MuiPaginationItem-root.Mui-selected": {
          color: "#fff",
          backgroundColor: PRIMARY_COLOR,
        },
        "& .MuiTablePagination-selectLabel": {
          fontSize: 11,
        },
        "& .MuiTablePagination-displayedRows": {
          fontSize: 11,
        },
        "& .MuiSelect-select": {
          fontSize: 11,
        },
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions,
      shape: "rounded",
      showFirstButton: false,
      showLastButton: false,
      SelectProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: 220,
              mt: 0.5,
              border: "1px solid #dfe6ed",
              borderRadius: 1.25,
              boxShadow: "0 8px 24px rgba(15, 23, 42, .14)",
              "& .MuiMenuItem-root": {
                minHeight: 34,
                fontFamily: "Inter, sans-serif",
                fontSize: 11,
              },
            },
          },
        },
      },
    },
    muiExpandButtonProps: {
      sx: {
        width: 20,
        height: 20,
        padding: 0,
        "& svg": { fontSize: 16 },
      },
    },
    initialState: {
      density: "compact",
      showGlobalFilter: false,
      ...initialState,
      pagination: {
        pageIndex: initialState?.pagination?.pageIndex ?? 0,
        pageSize: initialState?.pagination?.pageSize ?? 10,
      },
    },
    state: {
      ...state,
      globalFilter,
      pagination,
    },
    ...restTableOptions,
  });

  return <MaterialReactTable table={table} />;
}
